import React, { useState, useEffect } from 'react';
import {
	Grid,
	Card,
	FormControl,
	FormLabel,
	TextField,
	Typography,
	Button,
	Modal,
	Box,
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { withStyles } from '@material-ui/core/styles';
import axios from 'axios';
import { produce } from 'immer';
import { $format } from '../helpers/numberFormat';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ClearIcon from '@material-ui/icons/Clear';
import { green, red } from '@material-ui/core/colors';

function KioskSelfPay(props) {
	const [orders, setOrders] = useState([]);
	const [name, setName] = useState('');
	const [items, setItems] = useState([]);
	const [selectedChabura, setSelectedChabura] = useState();
	const [paymentResult, setPaymentResult] = useState();
	const [showNameError, setShowNameError] = useState(false);
	const [paymentSubmissionResult, setPaymentSubmissionResult] = useState();
	const [loading, setLoading] = useState(false);
	const [reloadCountdown, setReloadCountdown] = useState(5);
	const [activeQuantityItemId, setActiveQuantityItemId] = useState();

	const visibleItems = items.filter((i) => i.price > 0);
	const leftColumnItems = visibleItems.filter((_, index) => index % 2 === 0);
	const rightColumnItems = visibleItems.filter((_, index) => index % 2 === 1);
	const activeQuantityItem = items.find((item) => item.id === activeQuantityItemId);

	const total = React.useMemo(() => {
		return items.reduce((prev, curr) => {
			if (curr.quantity) {
				return prev + curr.price * curr.quantity;
			}
			return prev;
		}, 0);
	}, [items]);

	const blurActiveInput = () => {
		if (document.activeElement && typeof document.activeElement.blur === 'function') {
			document.activeElement.blur();
		}
	};

	const handleEnterBlur = (event) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			event.target.blur();
		}
	};

	const updateItemQuantity = (itemId, quantity) => {
		setPaymentResult('');
		setItems(
			produce(items, (draft) => {
				const matzahIndex = draft.findIndex((i) => i.id === itemId);
				if (matzahIndex >= 0) {
					draft[matzahIndex].quantity = quantity;
				}
			}),
		);
	};

	const appendQuantityDigit = (digit) => {
		if (!activeQuantityItem) {
			return;
		}

		const currentValue = activeQuantityItem.quantity?.toString() || '';
		if (digit === '.' && currentValue.includes('.')) {
			return;
		}
		if (digit === '.' && currentValue === '') {
			updateItemQuantity(activeQuantityItem.id, '0.');
			return;
		}
		const nextValue = currentValue === '0' ? digit : `${currentValue}${digit}`;
		updateItemQuantity(activeQuantityItem.id, nextValue);
	};

	const backspaceQuantityDigit = () => {
		if (!activeQuantityItem) {
			return;
		}

		const currentValue = activeQuantityItem.quantity?.toString() || '';
		updateItemQuantity(activeQuantityItem.id, currentValue.slice(0, -1));
	};

	const closeQuantityKeypad = () => {
		setActiveQuantityItemId(undefined);
	};

	const renderQuantityCard = (mt) => (
		<div
			key={mt.id}
			style={{
				display: 'flex',
				gap: 14,
				alignItems: 'center',
				justifyContent: 'space-between',
				width: '100%',
				padding: '14px 18px',
				borderRadius: 18,
				background: '#ffffff',
				boxShadow: '0 10px 24px rgba(31, 42, 68, 0.08)',
			}}
		>
			<div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
				<Typography style={{ fontWeight: 'bold', fontSize: 24 }}>{mt.matzahtype}</Typography>
				<Typography style={{ fontSize: 18, color: '#5c6783' }}>${mt.price} Per Pound</Typography>
			</div>
			<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
				<Button
					variant='outlined'
					onClick={() => setActiveQuantityItemId(mt.id)}
					style={{
						minWidth: 92,
						height: 58,
						borderRadius: 14,
						borderWidth: 2,
						fontSize: mt.quantity ? 24 : 16,
						fontWeight: 700,
						textTransform: 'none',
						backgroundColor: '#ffffff',
						color: mt.quantity ? '#1f2a44' : '#6b7691',
					}}
				>
					{mt.quantity || 'Enter'}
				</Button>
				<div style={{ width: 48, fontSize: 18, fontWeight: 700, textAlign: 'center' }}>LBS</div>
			</div>
		</div>
	);

	useEffect(() => {
		Promise.all([axios.get('/api/orders?status=RUNNING')]).then(([{ data: orders }]) => {
			setOrders(orders);
		});
	}, []);

	useEffect(() => {
		const order = orders.find((o) => o.chaburah_id.toString() === selectedChabura);

		if (order) {
			axios.get(`/api/orders/getPricesOfOrder?orderId=${order.id}`).then(({ data: items }) => {
				setItems(items.map((i) => ({ ...i, quantity: '' })));
			});
		}
	}, [selectedChabura]);

	useEffect(() => {
		if (!paymentSubmissionResult?.success) {
			return undefined;
		}

		setReloadCountdown(5);
		const countdownInterval = setInterval(() => {
			setReloadCountdown((current) => (current > 1 ? current - 1 : 1));
		}, 1000);
		const reloadTimer = setTimeout(() => {
			window.location.reload();
		}, 5000);

		return () => {
			clearInterval(countdownInterval);
			clearTimeout(reloadTimer);
		};
	}, [paymentSubmissionResult]);

	const onSubmitPayment = async () => {
		blurActiveInput();

		if (!name.trim()) {
			setShowNameError(true);
			return;
		}

		const order = orders.find((o) => o.chaburah_id.toString() === selectedChabura);

		const payment_items = items.map((matzahType, index) => ({
			amount: matzahType.quantity || 0,
			matzah_type_id: matzahType.id,
		}));
		const body = {
			orderId: order?.id,
			chaburaId: selectedChabura,
			last_name: name,
			discount: 0,
			amount: total,
			amountWithFee: (total + total * 0.03).toFixed(2),
			payment_items,
		};
		setLoading(true);
		setShowNameError(false);
		try {
			const { data } = await axios.post('/api/processPayment', body);
			if (data.status === 'Approved') {
				await axios.post(`/api/chaburos/${order?.chaburah_id}/orders/${order?.id}/payments/`, {
					last_name: name,
					discount: 0,
					amount: total,
					payment_items,
				});
				setPaymentSubmissionResult({
					success: true,
					message: 'Payment approved. Thank you.',
				});
			} else {
				setPaymentSubmissionResult({
					success: false,
					message: `${data.status}${data.error_message ? ` - ${data.error_message}` : ''}`,
				});
			}
		} catch (error) {
			const message =
				error.response?.data?.error_message || error.message || 'Unable to process payment.';
			setPaymentSubmissionResult({
				success: false,
				message,
			});
		}
		setLoading(false);
	};

	return (
		<React.Fragment>
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					gap: 18,
					alignItems: 'center',
					justifyContent: 'flex-start',
					minHeight: '100vh',
					padding: '18px 24px 24px',
					background: 'linear-gradient(180deg, #f4f7ff 0%, #ffffff 42%, #f8fbff 100%)',
				}}
			>
				<FormControl
					style={{
						width: '100%',
						maxWidth: 1100,
						padding: '24px 28px',
						border: selectedChabura ? '2px solid #3256d9' : '2px solid #cfd8f6',
						borderRadius: 24,
						background:
							'linear-gradient(135deg, rgba(232,237,255,1) 0%, rgba(244,247,255,1) 52%, rgba(255,255,255,1) 100%)',
						boxShadow: '0 16px 36px rgba(50, 86, 217, 0.14)',
					}}
				>
					<FormLabel
						style={{
							fontSize: 34,
							fontWeight: 800,
							color: '#ffffff',
							marginBottom: 14,
							textTransform: 'none',
							letterSpacing: 0,
							textAlign: 'center',
							background: 'linear-gradient(135deg, #2840a0 0%, #4164e8 100%)',
							padding: '14px 22px',
							borderRadius: 18,
							boxShadow: '0 10px 24px rgba(40, 64, 160, 0.28)',
						}}
					>
						Choose Your Chaburah
					</FormLabel>
					<div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center', marginTop: 24 }}>
						{orders.map((o) => (
							<Button
								key={o.chaburah_id}
								variant={selectedChabura === o.chaburah_id.toString() ? 'contained' : 'outlined'}
								color='primary'
								onClick={() => {
									setSelectedChabura(o.chaburah_id.toString());
								}}
								style={{
									margin: 0,
									padding: '18px 28px',
									borderRadius: 18,
									borderWidth: 2,
									borderColor:
										selectedChabura === o.chaburah_id.toString() ? '#3256d9' : '#b9c7ee',
									background:
										selectedChabura === o.chaburah_id.toString()
											? 'linear-gradient(135deg, #4164e8 0%, #2840a0 100%)'
											: '#ffffff',
									boxShadow:
										selectedChabura === o.chaburah_id.toString()
											? '0 10px 24px rgba(50, 86, 217, 0.28)'
											: 'none',
									minWidth: 220,
									minHeight: 82,
									fontSize: 28,
									fontWeight: 700,
									textTransform: 'none',
								}}
							>
								{o.nickname}
							</Button>
						))}
					</div>
				</FormControl>
				<div
					style={{
						display: 'flex',
						alignItems: 'flex-start',
						justifyContent: 'center',
						gap: 24,
						width: '100%',
						maxWidth: 1100,
						marginTop: 6,
					}}
				>
					<div
						style={{
							display: 'flex',
							gap: 18,
							flex: 2,
							minWidth: 0,
						}}
					>
						<div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minWidth: 0 }}>
							{leftColumnItems.map(renderQuantityCard)}
						</div>
						<div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minWidth: 0 }}>
							{rightColumnItems.map(renderQuantityCard)}
						</div>
					</div>
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							gap: 10,
							flexDirection: 'column',
							alignItems: 'stretch',
							padding: '18px 20px',
							borderRadius: 26,
							background: 'linear-gradient(180deg, #f3f7ff 0%, #ffffff 100%)',
							boxShadow: '0 18px 40px rgba(31, 42, 68, 0.12)',
							flex: 1,
							maxWidth: 320,
							minWidth: 275,
							border: '1px solid #d9e3ff',
						}}
					>
						{selectedChabura && (
							<div
								style={{
									padding: '4px 0 0',
								}}
							>
								<TextField
									label='Enter Your Name'
									value={name}
									variant='outlined'
									fullWidth
									onChange={(e) => {
										setName(e.target.value);
										if (showNameError && e.target.value.trim()) {
											setShowNameError(false);
										}
									}}
									onKeyDown={handleEnterBlur}
									InputLabelProps={{
										style: {
											fontSize: 19,
											fontWeight: 700,
											backgroundColor: '#ffffff',
											padding: '0 6px',
										},
									}}
									FormHelperTextProps={{
										style: {
											fontSize: 14,
											fontWeight: 600,
											marginTop: 6,
										},
									}}
									inputProps={{
										style: {
											fontSize: 24,
											padding: '16px 16px',
											backgroundColor: '#ffffff',
											borderRadius: 14,
										},
									}}
								/>
							</div>
						)}
						<div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
							<div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
								<Typography style={{ fontSize: 19 }}>Subtotal:</Typography>
								<Typography style={{ fontSize: 19 }}>{$format.format(total)}</Typography>
							</div>
							<div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
								<Typography style={{ fontSize: 19 }}>CC 3%:</Typography>
								<Typography style={{ fontSize: 19 }}>{$format.format(total * 0.03)}</Typography>
							</div>
							<div
								style={{
									marginTop: 8,
									paddingTop: 12,
									borderTop: '2px solid #dbe3fb',
									display: 'flex',
									gap: 8,
									justifyContent: 'space-between',
								}}
							>
								<Typography style={{ fontSize: 28, fontWeight: 800 }}>Total:</Typography>
								<Typography style={{ fontSize: 28, fontWeight: 800 }}>
									{$format.format(total + total * 0.03)}
								</Typography>
							</div>
						</div>
						<>
							<Button
								className={props.classes.button}
								variant='contained'
								size='large'
								disabled={total <= 0 || loading}
								onClick={onSubmitPayment}
								loading={loading}
								style={{
									minWidth: 220,
									minHeight: 72,
									fontSize: 22,
									fontWeight: 700,
									backgroundColor: total <= 0 || loading ? undefined : '#2e9b45',
									color: total <= 0 || loading ? undefined : '#ffffff',
								}}
							>
								Pay Now
							</Button>
						</>
					</div>
				</div>
			</div>
			<Modal open={loading} aria-labelledby='loading-modal'>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						bgcolor: 'background.paper',
						boxShadow: 24,
						p: 4,
						borderRadius: 2,
						textAlign: 'center',
					}}
				>
					<CircularProgress />
					<Typography variant='h6' sx={{ mt: 2 }}>
						Please finish selection on the credit card terminal
					</Typography>
				</Box>
			</Modal>
			<Modal open={!!activeQuantityItem} aria-labelledby='quantity-keypad-modal'>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						bgcolor: 'background.paper',
						boxShadow: 24,
						p: 4,
						borderRadius: 3,
						textAlign: 'center',
						width: 'min(420px, calc(100vw - 32px))',
					}}
				>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							width: '100%',
							marginBottom: 18,
						}}
					>
						<Typography style={{ fontSize: 28, fontWeight: 800 }}>
							{activeQuantityItem?.matzahtype} <span style={{ color: '#1f1f1f' }}>Pounds</span>
						</Typography>
						<Button
							variant='text'
							onClick={closeQuantityKeypad}
							style={{
								minWidth: 48,
								width: 48,
								height: 48,
								borderRadius: 999,
								fontSize: 28,
								fontWeight: 800,
								lineHeight: 1,
								padding: 0,
							}}
						>
							×
						</Button>
					</div>
					<div
						style={{
							width: '100%',
							padding: '16px 18px',
							borderRadius: 16,
							background: '#f5f7ff',
							fontSize: 36,
							fontWeight: 800,
							marginBottom: 18,
							textAlign: 'center',
							color: '#1f2a44',
						}}
					>
						{activeQuantityItem?.quantity || '0'}
					</div>
					<div
						style={{
							display: 'grid',
							gridTemplateColumns: '1fr auto',
							gridTemplateRows: 'auto auto',
							columnGap: 12,
							rowGap: 12,
							width: '100%',
						}}
					>
						<div
							style={{
								display: 'grid',
								gridTemplateColumns: 'repeat(3, 1fr)',
								gap: 12,
								gridRow: '1 / span 2',
							}}
						>
							{['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
								<Button
									key={digit}
									variant='outlined'
									onClick={() => appendQuantityDigit(digit)}
									style={{ minHeight: 72, fontSize: 28, fontWeight: 800 }}
								>
									{digit}
								</Button>
							))}
							<Button
								variant='outlined'
								onClick={backspaceQuantityDigit}
								style={{ minHeight: 72, fontSize: 28, fontWeight: 800, textTransform: 'none' }}
							>
								⌫
							</Button>
							<Button
								variant='outlined'
								onClick={() => appendQuantityDigit('0')}
								style={{ minHeight: 72, fontSize: 28, fontWeight: 800 }}
							>
								0
							</Button>
							<Button
								variant='outlined'
								onClick={() => appendQuantityDigit('.')}
								style={{ minHeight: 72, fontSize: 28, fontWeight: 800 }}
							>
								.
							</Button>
						</div>
						<Button
							variant='contained'
							color='primary'
							onClick={closeQuantityKeypad}
							style={{
								minWidth: 108,
								minHeight: 72,
								gridColumn: 2,
								gridRow: 2,
								alignSelf: 'end',
								fontSize: 22,
								fontWeight: 800,
							}}
						>
							Enter
						</Button>
					</div>
				</Box>
			</Modal>
			<Modal open={showNameError} aria-labelledby='name-required-modal'>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						bgcolor: 'background.paper',
						boxShadow: 24,
						p: 4,
						borderRadius: 3,
						textAlign: 'center',
						width: 'min(460px, calc(100vw - 32px))',
						border: '3px solid rgba(229, 57, 53, 0.2)',
					}}
				>
					<ClearIcon style={{ color: red[500], fontSize: 72, marginBottom: 12 }} />
					<Typography style={{ fontSize: 30, fontWeight: 800, marginBottom: 10 }}>
						Name Required
					</Typography>
					<Typography style={{ fontSize: 21, lineHeight: 1.4, marginBottom: 20 }}>
						Please enter your name before tapping Pay Now.
					</Typography>
					<Button
						variant='contained'
						color='primary'
						onClick={() => setShowNameError(false)}
						style={{ minWidth: 160, minHeight: 56, fontSize: 20, fontWeight: 700 }}
					>
						OK
					</Button>
				</Box>
			</Modal>
			<Modal open={!!paymentSubmissionResult} aria-labelledby='payment-result-modal'>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						bgcolor: 'background.paper',
						boxShadow: 24,
						p: 5,
						borderRadius: 3,
						textAlign: 'center',
						width: 'min(560px, calc(100vw - 32px))',
						border: paymentSubmissionResult?.success
							? '3px solid rgba(46, 155, 69, 0.35)'
							: '3px solid rgba(229, 57, 53, 0.28)',
					}}
				>
					{paymentSubmissionResult?.success ? (
						<CheckCircleOutlineIcon style={{ color: green[500], fontSize: 88, marginBottom: 16 }} />
					) : (
						<ClearIcon style={{ color: red[500], fontSize: 88, marginBottom: 16 }} />
					)}
					<Typography style={{ fontSize: 34, fontWeight: 800, marginBottom: 12 }}>
						{paymentSubmissionResult?.success ? 'Payment Successful' : 'Payment Not Completed'}
					</Typography>
					<Typography style={{ fontSize: 22, lineHeight: 1.4, marginBottom: 20 }}>
						{paymentSubmissionResult?.message}
					</Typography>
					{paymentSubmissionResult?.success ? (
						<Typography style={{ fontSize: 20, color: '#4e5a78' }}>
							Returning to the start screen in {reloadCountdown} second
							{reloadCountdown === 1 ? '' : 's'}.
						</Typography>
					) : (
						<div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
							<Button
								variant='contained'
								color='primary'
								onClick={() => setPaymentSubmissionResult(undefined)}
								style={{ minWidth: 200, minHeight: 58, fontSize: 20, fontWeight: 700 }}
							>
								Try Tapping Again
							</Button>
							<Button
								variant='outlined'
								onClick={() => window.location.reload()}
								style={{ minWidth: 180, minHeight: 58, fontSize: 20, fontWeight: 700 }}
							>
								Cancel
							</Button>
						</div>
					)}
				</Box>
			</Modal>
		</React.Fragment>
	);
}

const styles = (theme) => ({
	root: {
		minHeight: '100vh',
		margin: '100px auto',
	},
	orderCard: {
		height: '420px',
		padding: '20px',
		textAlign: 'center',
	},
	button: {
		height: '50px',
	},
});

export default withStyles(styles)(KioskSelfPay);
